import { useMemo } from 'react';
import { useGuardedFetch } from '../usePerformance';
import { v2PeriodData } from './v2Metrics';

/**
 * The account-level metrics behind "Account read".
 *
 * Reuses useGuardedFetch rather than re-implementing a fetch: the stale-response
 * guard it carries is exactly as load-bearing here as on the card tabs, because
 * this view is also reachable while an account switch is in flight, and a late
 * response from the previous account would paint the previous client's spend
 * under the current client's name. That is the one failure this page must not
 * have.
 *
 * Legacy mode uses three calls on load, plus one for a picked range. V2 uses
 * only /v2-summary and derives every chart/comparison from its daily evidence.
 * All calls are GET. Nothing here dispatches a pull — the same read-only
 * contract the rest of the section holds to. A metrics refresh costs money and
 * belongs to the Campaign Grid's own explicit Refresh button.
 *
 * There was a third — /api/v2/pacing, for the media plans' booked total — and it
 * went with the pacing card it fed. Nothing else on this view reads a booked
 * figure, so the call was pure cost.
 *
 *   /api/v2/campaign-grid       campaigns + their cached metrics — the account
 *                               totals and the campaign table.
 *   /api/v2/performance/series  the per-day delivery series, read out of the
 *                               last grid-pull's GCS artifact. The grid's own
 *                               table cannot serve this — it keeps one row per
 *                               (campaign, platform) with no date dimension.
 *   /api/v2/performance/campaign-deltas
 *                               the SAME artifact split into two halves PER
 *                               campaign — the "was → now" behind the CPA
 *                               deterioration list. Cheap alongside /series:
 *                               both read one per-run cache, so the artifact is
 *                               downloaded and parsed once.
 *   /api/v2/performance/windowed-metrics
 *                               the SAME artifact rolled up per campaign for the
 *                               picked date range. Fetched only when a range is
 *                               picked; without one the page shows the grid's
 *                               cached numbers as before.
 *
 * ON THE WINDOW. It is applied to the artifact's per-day rows, NOT by re-pulling
 * — that is the whole point. The Campaign Grid re-windows by dispatching a fresh
 * reporting-API pull that overwrites the shared campaign_grid_metrics row; doing
 * that from here would cost money on a picker change and change what the grid
 * shows for everyone else on the account.
 */
export function usePortfolio(accountId, userId, campaignId = null, compare = false, dateWindow = null, performanceV2 = false, campaignIds, savedDataset) {
  const ready = Boolean(accountId && userId);
  // Omitted entirely when there is no window, so the default request stays
  // byte-identical to the pre-picker one.
  const windowParams = dateWindow
    ? {
      ...(dateWindow.startDate ? { startDate: dateWindow.startDate } : {}),
      ...(dateWindow.endDate ? { endDate: dateWindow.endDate } : {}),
    }
    : {};

  const fetchedGrid = useGuardedFetch(
    ready && !savedDataset ? (performanceV2 ? '/api/v2/performance/v2-summary' : '/api/v2/campaign-grid') : null,
    { accountId, userId, ...(performanceV2 ? windowParams : {}) },
  );
  const grid = savedDataset ? { data: savedDataset, loading: false, error: null } : fetchedGrid;

  // `compare` is a SERVER-side choice, not a client filter: comparing halves the
  // pull window, so the two modes return different numbers of days and the split
  // has to happen where the days are assembled. Omitted when off so the default
  // request stays byte-identical.
  const series = useGuardedFetch(
    ready && !performanceV2 ? '/api/v2/performance/series' : null,
    {
      accountId,
      userId,
      ...(campaignId ? { campaignId } : {}),
      ...(compare ? { compare: 'split' } : {}),
      // The chart is clamped to the picked range too — a trend line covering a
      // different span than the tiles above it invites exactly the misreading
      // this page exists to avoid.
      ...windowParams,
    },
  );

  // Per-campaign period-over-period comparison. Always requested, unlike the
  // roll-up below: it is not a re-window of the grid's numbers but the only
  // source of a prior period there is, so without it the list has nothing to
  // show at any range — including the default one.
  const deltas = useGuardedFetch(
    ready && !performanceV2 ? '/api/v2/performance/campaign-deltas' : null,
    { accountId, userId, ...windowParams },
  );

  // Per-campaign totals for the picked range. Not requested at all without a
  // range: with no window the page reads the grid's cached numbers, which is
  // both cheaper and the only thing that can cover non-Google campaigns.
  const windowed = useGuardedFetch(
    ready && dateWindow && !performanceV2 ? '/api/v2/performance/windowed-metrics' : null,
    { accountId, userId, ...windowParams },
  );

  const campaigns = useMemo(() => grid.data?.campaigns || [], [grid.data]);
  const v2Windowed = useMemo(() => performanceV2 && dateWindow && grid.data ? {
    campaigns: Object.fromEntries(campaigns.filter((c) => c.performanceV2?.days).map((c) =>
      [c.id, { ...c.performanceV2.metrics, days: c.performanceV2.days }])),
    window: dateWindow,
  } : null, [performanceV2, dateWindow, grid.data, campaigns]);

  const periods = useMemo(() => performanceV2 && grid.data ? v2PeriodData(
    campaignIds !== undefined ? campaigns.filter(c => !campaignIds.length || campaignIds.includes(c.id))
      : campaignId ? campaigns.filter((c) => c.id === campaignId) : campaigns, compare,
  ) : null, [performanceV2, grid.data, campaignId, campaignIds, campaigns, compare]);

  // The original grid response and chart/comparison evidence for the raw-JSON
  // export (v2 derived objects identify their source). Memoized so PortfolioTab can
  // hand it up in an effect that fires when a response lands, not on every
  // render — an unmemoized object there would loop the parent's setState.
  const raw = useMemo(() => ({
    grid: grid.data || null,
    series: performanceV2 ? periods?.series || null : series.data || null,
    deltas: performanceV2 ? periods?.deltas || null : deltas.data || null,
    windowed: dateWindow ? (performanceV2 ? v2Windowed : windowed.data || null) : null,
    dateWindow: dateWindow || null,
    errors: {
      grid: grid.error || null,
      series: (performanceV2 ? grid.error : series.error) || null,
      deltas: (performanceV2 ? grid.error : deltas.error) || null,
      windowed: dateWindow ? ((performanceV2 ? grid.error : windowed.error) || null) : null,
    },
  }), [grid.data, grid.error, series.data, series.error, deltas.data, deltas.error,
    windowed.data, windowed.error, dateWindow, performanceV2, v2Windowed, periods]);

  return {
    raw,
    artifactsUnreadable: grid.data?.artifactsUnreadable || 0,
    // The grid gates the page because everything else on it is per-campaign.
    // The series costs exactly one card, so it is not allowed to hold the page
    // in a loading state or blank it on failure.
    loading: grid.loading,
    error: grid.error,
    campaigns,
    series: performanceV2 ? periods?.series || null : series.data || null,
    seriesLoading: performanceV2 ? grid.loading : series.loading,
    seriesError: performanceV2 ? grid.error : series.error,
    deltas: performanceV2 ? periods?.deltas || null : deltas.data || null,
    deltasLoading: performanceV2 ? grid.loading : deltas.loading,
    deltasError: performanceV2 ? grid.error : deltas.error,
    // Null until a range is picked. The view distinguishes "no window" from "a
    // window whose roll-up has not landed yet" — showing lifetime numbers under
    // a range label for even one render is the misread to avoid.
    windowed: dateWindow ? (performanceV2 ? v2Windowed : windowed.data || null) : null,
    windowedLoading: Boolean(dateWindow) && (performanceV2 ? grid.loading : windowed.loading),
    windowedError: dateWindow ? (performanceV2 ? grid.error : windowed.error) : null,
  };
}

export default usePortfolio;
